/**
 * Network Hierarchy API Service - Cliente para endpoints de Jerarquía Morfológica
 *
 * Este servicio proporciona acceso al endpoint de análisis de jerarquía
 * morfológica de red basado en métricas de sintaxis espacial.
 *
 * @example
 * ```typescript
 * // Obtener análisis con perfil equilibrado
 * const result = await networkHierarchyApi.getAnalysis(projectId, 'equilibrado');
 *
 * // Features con clasificación por deciles
 * result.features.forEach(f => {
 *   console.log(f.properties.hierarchyDecile); // 1-10
 *   console.log(f.properties.hierarchyBand);   // 'alta' | 'media' | 'baja'
 * });
 * ```
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;
    const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
    return parsed.state?.token || null;
  } catch {
    return null;
  }
};

/**
 * Crea headers con autenticación
 */
const getHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Perfiles de análisis disponibles
 */
export type HierarchyProfile = 'global' | 'local' | 'equilibrado';

/**
 * Propiedades de un segmento clasificado
 */
export interface HierarchyFeatureProperties {
  edgeId: string;
  lengthMeters: number | null;
  isShortSegment: boolean;
  globalScore: number;
  localScore: number;
  combinedScore: number;
  combinedPercentile: number;
  hierarchyDecile: number;
  hierarchyBand: 'alta' | 'media' | 'baja';
  rawNainGlobal: number | null;
  rawNainLocal: number | null;
}

/**
 * Feature individual con geometría LineString
 */
export interface HierarchyFeature {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: Array<[number, number]>;
  };
  properties: HierarchyFeatureProperties;
}

/**
 * Distribución por decil
 */
export interface DecileDistribution {
  decile: number;
  count: number;
  km: number;
  percentOfTotal: number;
}

/**
 * Distribución por banda
 */
export interface BandDistribution {
  band: 'alta' | 'media' | 'baja';
  count: number;
  km: number;
  percentOfTotal: number;
  avgCombinedScore: number;
}

/**
 * Distribución por rangos de longitud
 */
export interface LengthDistribution {
  below20m: {
    count: number;
    km: number;
    percent: number;
  };
  between20_50m: {
    count: number;
    km: number;
    percent: number;
  };
  above50m: {
    count: number;
    km: number;
    percent: number;
  };
  unknown: {
    count: number;
    percent: number;
  };
}

/**
 * Metadata del análisis
 */
export interface HierarchyMetadata {
  projectId: string;
  syntaxRunId: string;
  completedAt: string;
  analysisProfile: HierarchyProfile;
  weights: {
    global: number;
    local: number;
  };
  totalSegments: number;
  totalKm: number;
  skippedMetricsCount: number;
  skippedMissingEdgeCount: number;
  skippedInvalidGeometryCount: number;
  distributionByDecile: DecileDistribution[];
  distributionByBand: BandDistribution[];
  segmentLengthDistribution: LengthDistribution;
}

/**
 * Response completo: GeoJSON FeatureCollection
 */
export interface NetworkHierarchyResponse {
  type: 'FeatureCollection';
  features: HierarchyFeature[];
  metadata: HierarchyMetadata;
}

// ============================================================================
// API CLIENT
// ============================================================================

export const networkHierarchyApi = {
  /**
   * Obtiene el análisis de jerarquía morfológica para un proyecto
   *
   * @param projectId - ID del proyecto
   * @param profile - Perfil de análisis (default: 'equilibrado')
   * @returns GeoJSON FeatureCollection con segmentos clasificados y metadata
   */
  async getAnalysis(
    projectId: string,
    profile: HierarchyProfile = 'equilibrado',
  ): Promise<NetworkHierarchyResponse> {
    const response = await axios.get<NetworkHierarchyResponse>(
      `${API_URL}/projects/${projectId}/network/hierarchy`,
      {
        params: { profile },
        headers: getHeaders(),
        timeout: 60000, // 60s — análisis de 37K segmentos puede tardar
      },
    );
    return response.data;
  },
};
